import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { FileSpreadsheet, FileText, Loader2, Map, Upload } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import type { WizardAction, WizardState } from "../ImportPage";

interface Props {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
}

export function UploadStep({ state, dispatch }: Props) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [pendingBase64, setPendingBase64] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const parseMutation = trpc.import.parseFile.useMutation({
    onSuccess: (result) => {
      // If XLSX with multiple sheets and no sheet selected yet, show picker
      if (result.sheetNames && result.sheetNames.length > 1 && !selectedSheet) {
        setSheetNames(result.sheetNames);
        setSelectedSheet(result.selectedSheet ?? result.sheetNames[0] ?? "");
        // Still dispatch — user can re-parse with different sheet
        dispatch({
          type: "FILE_PARSED",
          fileData: pendingBase64,
          fileName: selectedFile?.name ?? "",
          parseResult: result,
        });
      } else {
        dispatch({
          type: "FILE_PARSED",
          fileData: pendingBase64,
          fileName: selectedFile?.name ?? "",
          parseResult: result,
        });
      }
    },
    onError: (err) => {
      dispatch({ type: "SET_ERROR", error: err.message });
    },
  });

  const readAndParse = useCallback(
    (file: File, sheet?: string) => {
      setSelectedFile(file);
      dispatch({ type: "SET_LOADING", isLoading: true });

      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1] ?? "";
        setPendingBase64(base64);
        parseMutation.mutate({
          fileData: base64,
          fileName: file.name,
          selectedSheet: sheet,
        });
      };
      reader.onerror = () => {
        dispatch({ type: "SET_ERROR", error: "Failed to read file." });
      };
      reader.readAsDataURL(file);
    },
    [dispatch, parseMutation],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file) readAndParse(file);
    },
    [readAndParse],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) readAndParse(file);
    },
    [readAndParse],
  );

  const handleSheetChange = useCallback(
    (sheet: string) => {
      setSelectedSheet(sheet);
      if (selectedFile) {
        readAndParse(selectedFile, sheet);
      }
    },
    [selectedFile, readAndParse],
  );

  const isLoading = state.isLoading || parseMutation.isPending;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Data Source</Label>
            <RadioGroup
              value={state.sourceType}
              onValueChange={(val) =>
                dispatch({
                  type: "SET_SOURCE_TYPE",
                  sourceType: val as "city" | "external_import",
                })
              }
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="external_import" id="source-external" />
                <Label
                  htmlFor="source-external"
                  className="cursor-pointer text-sm"
                >
                  External Import
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="city" id="source-city" />
                <Label htmlFor="source-city" className="cursor-pointer text-sm">
                  City Data
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            className={`
              border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer
              ${dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50"}
            `}
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.zip"
              onChange={handleFileSelect}
              className="hidden"
            />

            {isLoading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
                <p className="text-sm text-muted-foreground">Parsing file...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Upload className="w-10 h-10 text-muted-foreground/60" />
                <div>
                  <p className="text-base font-medium text-foreground">
                    Drop your file here or click to browse
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Supports CSV, XLSX, and Shapefile (ZIP) files
                  </p>
                </div>
                <div className="flex gap-4 mt-2">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <FileText className="w-4 h-4" />
                    <span>CSV</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <FileSpreadsheet className="w-4 h-4" />
                    <span>XLSX</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Map className="w-4 h-4" />
                    <span>Shapefile (ZIP)</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sheet selector for XLSX with multiple sheets */}
          {sheetNames.length > 1 && (
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm font-medium text-foreground">
                Sheet:
              </span>
              <Select value={selectedSheet} onValueChange={handleSheetChange}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sheetNames.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {state.error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {state.error}
        </div>
      )}

      {selectedFile && !isLoading && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          {selectedFile.name.endsWith(".csv") ? (
            <FileText className="w-4 h-4" />
          ) : selectedFile.name.endsWith(".zip") ? (
            <Map className="w-4 h-4" />
          ) : (
            <FileSpreadsheet className="w-4 h-4" />
          )}
          <span>
            {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
          </span>
        </div>
      )}
    </div>
  );
}
